// PaywallFaceShape - copy of PaywallHairStyle (/hairstyle)
import React, { useState, useRef, useEffect } from "react";
import { container } from "./../../constants/styles";
import welcomeImg from "./../../assets/images/faceshape_hero.jpeg";
import readyImg from "./../../assets/images/ready.png";
import buttonImg from "./../../assets/images/button.png";
import stopImg from "./../../assets/images/stop_wasting.png";
import beforeImg from "./../../assets/images/style_beforeafter.jpeg";

import freeGiftImg from "./../../assets/images/free_gift.png";
import analyzetImg from "./../../assets/images/analyzet_faceshape.png";
import toolsImg from "./../../assets/images/tools.png";
import seeImg from "./../../assets/images/see.png";
import resultImg from "./../../assets/images/result.png";
import mediaImg from "./../../assets/images/media.png";
import bloggersImg from "./../../assets/images/bloggers.png";
import visaImg from "./../../assets/images/mb_visa.png";
import payIcon1 from "./../../assets/images/icons_payment/ic1.svg";
import payIcon2 from "./../../assets/images/icons_payment/ic2.svg";
import payIcon3 from "./../../assets/images/icons_payment/ic3.svg";
import payIcon4 from "./../../assets/images/icons_payment/ic4.svg";
import payIcon5 from "./../../assets/images/icons_payment/ic5.svg";
import payIcon6 from "./../../assets/images/icons_payment/ic6.svg";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import Form from "../../components/Form/Form";
import { useSelector } from "react-redux";
import Button from "../../components/Button/Button";
import Popup from "../../components/Popup/Popup";
import AppleGooglePayButton from "../../components/AppleGooglePayButton/AppleGooglePayButton";
import { getPaywallPricingContext, isCanonicalPaywallRoute } from "../../utils/paywallPricing";
import { isOrganicOrSeoTraffic } from "../../utils/utm";
import { navigateToPostPaymentUpsell } from "../../utils/postPaymentUpsell";
import NotificationCarouselFaceShape from "../../components/NotificationCarousel/NotificationCarouselFaceShape";
import UserPhotoHeroShowcase from "./UserPhotoHeroShowcase";
import { useScrollToRef } from "../../hooks/useScrollToRef";
import {
  STRIPE_PRICE_ID,
  STRIPE_PUBLIC_KEY,
} from "../../constants/formConstants";
import discountImg from "./../../assets/images/common/discount.svg";
import { useTimer } from "../../hooks/useTimer";
import { useIsInViewport } from "../../hooks/useIsInViewPort";
import cosmoImg from "./../../assets/images/cosmo.png";
import vogueImg from "./../../assets/images/vogue.png";
import marieclaireImg from "./../../assets/images/marieclaire.png";
import elleImg from "./../../assets/images/elle.png";
import { useClientReferenceCookie } from "../../hooks/useClientReferenceCookie";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, ShieldCheck } from "lucide-react";
import SubscriptionDisclaimer from "./SubscriptionDisclaimer";

const PAYMENT_METHOD_ICONS = [
  { src: payIcon1, alt: "Payment method" },
  { src: payIcon2, alt: "Payment method" },
  { src: payIcon3, alt: "Payment method" },
  { src: payIcon4, alt: "Payment method" },
  { src: payIcon5, alt: "Payment method" },
  { src: payIcon6, alt: "Payment method" },
];

const FACESHAPE_USER_PHOTO_TEASERS = [
  { label: "Best glasses", score: "97%", previewType: "glasses" },
  { label: "Best hairstyle", score: "96%", previewType: "hairstyle" },
  { label: "Best contouring", score: "94%", previewType: "contouring" },
];

function PaywallFaceShape({ useUserPhotoHero = false }) {
  const pricing = getPaywallPricingContext(window.location.pathname, window.location.search);
  const isOrganicDisclaimer =
    isCanonicalPaywallRoute(window.location.pathname) &&
    isOrganicOrSeoTraffic(window.location.search);
  const navigate = useNavigate();
  const discountBlockRef = useRef(null);
  const heresWhatYouGetRef = useRef(null);
  const formattedTime = useTimer(10);

  const scrollToRef = useScrollToRef();
  const { elementRef: trustSectionRef, isInViewPort: isTrustSectionVisible, wasInViewPort: wasTrustSectionVisible } = useIsInViewport();
  
  const [showStickyButton, setShowStickyButton] = useState(false);

  useClientReferenceCookie();

  // Set page title and meta description
  useEffect(() => {
    document.title = "Get Your Face Shape + Style Guide for only $1";
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }
    metaDescription.content =
      "Get your face shape result plus a haircut, glasses, and makeup guide tailored to your proportions.";
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (heresWhatYouGetRef.current) {
        const rect = heresWhatYouGetRef.current.getBoundingClientRect();
        // Show button only when user has scrolled past the section start (section top is at or above viewport top)
        const isAtOrBelowSection = rect.top <= 0;
        setShowStickyButton(isAtOrBelowSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const landingPlans = [
    {
      value: pricing.trialCost,
      text: pricing.trialDisplay,
      trialValue: pricing.trialValue,
      trialValueText: pricing.trialValueText,
      willBeText:
        "Then your yearly Subscription Period will start and you will be charged $27.99 every quarter. No refunds if canceled before the end of Subscription Period.",
    },
    {
      value: "9.78",
      text: "$9.78",
      trialValue: 9.78,
      trialValueText: "$9.78",
      willBeText:
        "Then your yearly Subscription Period will start and you will be charged $27.99 every quarter. No refunds if canceled before the end of Subscription Period.",
    },
  ];

  const [stripePromise, setStripePromise] = useState(() =>
    loadStripe(STRIPE_PUBLIC_KEY)
  );

  // V1 route always has $1 plan preselected
  const [plan, setPlan] = useState(pricing.trialCost);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [showPromoCode, setShowPromoCode] = useState(false);

  const isApplePayAvailible = useSelector(
    (state) => state.payment.isApplePayAvailible
  );

  const onGetClick = () => {
    if (heresWhatYouGetRef.current) {
      scrollToRef(heresWhatYouGetRef, 100);
      return;
    }
  };

  const onPlanClick = (plan) => {
    setPlan(plan.value);
  };

  const onClosePopupClick = () => {
    setPopupOpen(false);
  };

  const onPopupOpenClick = () => {
    setPopupOpen(true);
  };

  const onSuccess = (paymentIntentId) => {
    navigateToPostPaymentUpsell(navigate, "/getofferhair", paymentIntentId);
  };

  const renderFullForm = (onlyApplePay) => {
    return (
      <div className={`max-w-[450px] mx-auto`}>
        <Elements stripe={stripePromise}>
          <Form
            priceID={STRIPE_PRICE_ID}
            trialCost={plan}
            paymentAmount={pricing.amount}
            paymentCurrency={pricing.currency}
            paymentValue={pricing.purchaseValue}
            littleText={"little text"}
            title={""}
            onlyApplePay={onlyApplePay}
            successAction={onSuccess}
          />
        </Elements>
      </div>
    );
  };

  const renderPopupForm = () => {
    return (
      <div className="font-[var(--funnel-cs-font-body)] text-[#2B2622]">
        <h3 className="mb-2 font-[var(--funnel-cs-font-heading)] text-[28px] font-[600] leading-tight text-[#2B2622]">
          Payment method
        </h3>
        <p className="text-[12px] text-[#6F665D]">
          {" "}
          You will only be charged {pricing.trialDisplay}
        </p>
        {renderFullForm()}
      </div>
    );
  };

  const TimerRender = () => {
    return (
      <div className="bg-[#FBF8F3] border-b border-[#EAD9CD] h-14 w-full flex items-center">
        <div className="font-[var(--funnel-cs-font-body)] text-center w-full">
          <p className="text-sm font-medium text-[#6F665D] leading-normal">
            Face Shape + Style Guide only <span className="font-bold text-[#9A2E1A]">{pricing.trialDisplay}!</span> Offer ends in:
          </p>
          <p className="text-2xl font-extrabold text-[#9A2E1A] leading-tight tracking-tight">
            {formattedTime}
          </p>
        </div>
      </div>
    );
  };

  const getSeasonalPromoCode = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    let season = "";

    if (month >= 3 && month <= 5) {
      season = "SPRING";
    } else if (month >= 6 && month <= 8) {
      season = "SUMMER";
    } else if (month >= 9 && month <= 11) {
      season = "FALL";
    } else {
      season = "WINTER";
    }

    return `${season}${year}`;
  };

  const discountBlockRender = () => {
    return (
      <div>
        <div className="mb-1 flex items-center justify-between">
          <p className="text-[18px] font-[800] text-[#2B2622]">
            Promo code applied
          </p>
          <div
            onClick={() => scrollToRef(discountBlockRef, 100)}
            className="flex cursor-pointer items-center rounded-[8px] bg-[#9A2E1A] px-3 py-[10px] text-[18px] font-[800] text-[#ffffff]"
          >
            <img src={discountImg} className="mr-1 min-w-5" alt="discount" />
            <p>{getSeasonalPromoCode()}</p>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-[16px] font-[500] text-[#2B2622]">
            Promo code reserved for:
          </p>
          <p className="text-[16px] font-[500] text-[#2B2622]">
            {formattedTime}
          </p>
        </div>
      </div>
    );
  };

      return (
      <div>
      {/* Sticky wrapper for notification and timer sections */}
      <div className="sticky top-0 z-50">
        {/* Top notification banner */}
        <div className="w-full bg-[#F6F1EB] border-b border-[#EAD9CD] cursor-pointer" onClick={onGetClick}>
          <NotificationCarouselFaceShape />
        </div>
        
        {/* Timer section */}
        <div className="w-full bg-[#FBF8F3] border-b border-[#EAD9CD] cursor-pointer" onClick={onGetClick}>
          <div className="py-2 font-[var(--funnel-cs-font-body)] text-center">
            <p className="text-sm font-medium text-[#6F665D] leading-normal">
              Face Shape + Style Guide only <span className="font-bold text-[#9A2E1A]">{pricing.trialDisplay}!</span> Offer ends in:
            </p>
            <p className="text-lg font-extrabold text-[#9A2E1A] leading-tight tracking-tight">
              {formattedTime}
            </p>
          </div>
        </div>
      </div>
      
      <div className={`${container} pt-3 pb-6`}>

      <div className="flex justify-center mb-2">
        <div className="text-center max-w-[500px] font-[var(--funnel-cs-font-body)]">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-[600] font-[var(--funnel-cs-font-heading)] text-[#2B2622] leading-tight tracking-tight mb-0.5">
            Your Face Shape +
          </h2>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-[600] font-[var(--funnel-cs-font-heading)] text-[#9A2E1A] leading-tight tracking-tight mb-1">
            Style Guide
          </h2>
          <div className="flex items-center justify-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-[600] font-[var(--funnel-cs-font-heading)] text-[#2B2622] leading-tight tracking-tight mr-2">
              are Ready
            </h2>
            <span className="text-2xl sm:text-3xl md:text-4xl">😍</span>
          </div>
        </div>
      </div>

      <div className="flex justify-center mb-3 px-2">
        {useUserPhotoHero ? (
          <UserPhotoHeroShowcase
            badgeText="Face shape detected"
            fallbackSrc={welcomeImg}
            teaserVariant="recommendation"
            teasers={FACESHAPE_USER_PHOTO_TEASERS}
          />
        ) : (
          <img src={welcomeImg} alt="welcome" className="max-w-full w-full rounded-lg" />
        )}
      </div>

      <div className="flex justify-center mb-3">
        <Button
          buttonStyles={`bg-[#9A2E1A] hover:bg-[#762514] text-[#FBF8F3] px-8 py-3 rounded-[14px] font-bold text-base max-w-[350px] mb-4 inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#9A2E1A] focus:ring-offset-2 transition-colors duration-200`}
          text="GET MY FACE SHAPE GUIDE NOW!"
          blick={false}
          action={() => onGetClick()}
        />
      </div>

      <div className="flex justify-center mb-8 px-2">
        <div ref={heresWhatYouGetRef} className="max-w-[900px] w-full rounded-[28px] border border-[#EAD9CD] bg-gradient-to-br from-[#FFFFFF] to-[#F6F1EB] p-6 shadow-[var(--funnel-cs-shadow-card)] font-[var(--funnel-cs-font-body)]">
          <h2 className="text-lg sm:text-xl font-[600] font-[var(--funnel-cs-font-heading)] text-center mb-6">
            <span className="text-[#2B2622]">Here's What </span>
            <span className="text-[#9A2E1A]">You Get</span>
          </h2>
          
          <div className="space-y-2 mb-3">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-[#F3E2DB] rounded-sm flex items-center justify-center mr-3 mt-0.5">
                <span className="text-[#9A2E1A] text-xs font-bold">✓</span>
              </div>
              <p className="text-base text-[#6F665D] leading-relaxed text-left">
                <span className="font-bold text-[#9A2E1A]">Your face shape result </span>
                <span className="font-normal text-[#6F665D]">(with a simple explanation)</span>
              </p>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-[#F3E2DB] rounded-sm flex items-center justify-center mr-3 mt-0.5">
                <span className="text-[#9A2E1A] text-xs font-bold">✓</span>
              </div>
              <p className="text-base text-[#6F665D] leading-relaxed text-left">
                <span className="font-bold text-[#9A2E1A]">Haircuts that flatter your proportions</span> — top picks + what to avoid
              </p>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-[#F3E2DB] rounded-sm flex items-center justify-center mr-3 mt-0.5">
                <span className="text-[#9A2E1A] text-xs font-bold">✓</span>
              </div>
              <p className="text-base text-[#6F665D] leading-relaxed text-left">
                <span className="font-bold text-[#9A2E1A]">Glasses guide</span> — best frame shapes + fit tips (width/bridge/lens height)
              </p>
            </div>
            
            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-[#F3E2DB] rounded-sm flex items-center justify-center mr-3 mt-0.5">
                <span className="text-[#9A2E1A] text-xs font-bold">✓</span>
              </div>
              <p className="text-base text-[#6F665D] leading-relaxed text-left">
                <span className="font-bold text-[#9A2E1A]">Makeup placement map</span> — blush, contour, highlight + brow direction
              </p>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-5 h-5 bg-[#F3E2DB] rounded-sm flex items-center justify-center mr-3 mt-0.5">
                <span className="text-[#9A2E1A] text-xs font-bold">✓</span>
              </div>
              <p className="text-base text-[#6F665D] leading-relaxed text-left">
                <span className="font-bold text-[#9A2E1A]">Bonus personalized tips</span> — quick, practical styling advice for you
              </p>
            </div>
          </div>

          {/* Promo Code Section */}
          <div className="bg-[#F3E2DB] rounded-[18px] p-3 mb-3 border border-[#E5C9BD]">
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 bg-[#9A2E1A] rounded-[14px] flex items-center justify-center mr-3">
                <span className="text-white text-lg">🎁</span>
              </div>
              <div>
                <p className="font-bold text-[#9A2E1A] text-sm">Promo Code Season26 Applied</p>
                <p className="text-sm text-[#6F665D] text-left">You save 85%</p>
              </div>
            </div>
          </div>

          {/* Pricing Section */}
          <div className="flex justify-between items-center mb-3 pb-4 border-b border-[#9A2E1A]/20">
            <span className="text-sm font-semibold text-[#2B2622]">Total due today:</span>
            <div className="text-right">
              <span className="text-sm text-[#8A8075] line-through mr-2">{pricing.trialOriginalDisplay}</span>
              <span className="text-lg font-bold text-[#9A2E1A]">{pricing.trialDisplay}</span>
            </div>
          </div>

          <p className="mb-3 text-center text-sm font-medium text-[#6F665D]">
            30-day money back guarantee
          </p>

          {/* Payment Buttons */}
          <div className="space-y-1 mb-6">
            {/* Apple Pay and Google Pay */}
            <Elements stripe={stripePromise}>
              <AppleGooglePayButton
                trialCost={plan}
            paymentAmount={pricing.amount}
            paymentCurrency={pricing.currency}
            paymentValue={pricing.purchaseValue}
                onSuccess={onSuccess}
                isDisabled={false}
              />
            </Elements>
            
            {/* Credit/Debit Card Button */}
            <Button
              buttonStyles="w-full bg-[#9A2E1A] hover:bg-[#762514] text-[#FBF8F3] px-6 py-4 rounded-[14px] font-semibold text-base inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#9A2E1A] focus:ring-offset-2 transition-colors duration-200"
              text="💳 Credit or Debit Card"
              isButtonDisabled={false}
              action={() => onPopupOpenClick()}
            />
          </div>

          {/* Terms Text */}
          <SubscriptionDisclaimer
            price={pricing.subscriptionDisclaimerPrice}
            className="text-[#8A8075] text-xs leading-relaxed text-left"
            isOrganicOrSeo={isOrganicDisclaimer}
            billingPeriod="every 3 months"
          />

          {/* PaySafe + Payment Methods + Guarantee */}
          <div className="mt-5">
            <div className="flex flex-col items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#E5C9BD]/40 bg-[#E5C9BD]/15 px-4 py-2 font-[var(--funnel-cs-font-body)]">
                <ShieldCheck className="h-5 w-5 text-[#7A3F22]" aria-hidden="true" />
                <span className="text-sm font-semibold text-[#7A3F22]">
                  Pay Safe &amp; Secure
                </span>
              </div>

              <div className="flex items-center justify-center gap-2 flex-wrap">
                {PAYMENT_METHOD_ICONS.map((icon, idx) => (
                  <div
                    key={`${icon.src}-${idx}`}
                    className="flex h-9 w-14 items-center justify-center rounded-[14px] border border-[#EAD9CD] bg-white shadow-sm"
                  >
                    <img
                      src={icon.src}
                      alt={icon.alt}
                      className="h-5 w-auto max-w-[44px]"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 relative overflow-hidden rounded-[18px] border border-[#E5C9BD] bg-[#FBF8F3] p-5 shadow-sm">
              <div className="mx-auto max-w-[520px] text-center font-[var(--funnel-cs-font-body)]">
                <h3 className="text-xl font-[600] font-[var(--funnel-cs-font-heading)] text-[#2B2622]">
                  30-day money-back guarantee
                </h3>
                <p className="mt-2 text-base text-[#6F665D] leading-relaxed">
                  If you reach out within 30 days of purchase, we'll give you a
                  full refund.
                </p>
                <a
                  href="https://my-stylist.pro/terms#refund"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-block text-base font-semibold text-[#2B2622] underline decoration-[#9A2E1A] underline-offset-4 hover:text-[#9A2E1A]"
                >
                  Learn more
                </a>
              </div>

              <div className="pointer-events-none absolute -right-6 -bottom-6 h-20 w-20 rounded-full bg-[#E5C9BD]/20 flex items-center justify-center">
                <BadgeCheck className="h-10 w-10 text-[#7A3F22]" aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Section with Frame */}
      <div ref={trustSectionRef} className="flex justify-center mb-8 px-2">
        <div className="max-w-[900px] w-full rounded-[28px] border border-[#EAD9CD] bg-gradient-to-br from-[#FFFFFF] to-[#F6F1EB] p-6 shadow-[var(--funnel-cs-shadow-card)] font-[var(--funnel-cs-font-body)]">
          
          {/* Statistics Section */}
          <div className="mb-6 pb-4 border-b border-[#9A2E1A]/20">
            <div className="text-left">
              <p className="text-lg font-medium text-[#2B2622] mb-1">Over <span className="font-bold text-[#9A2E1A]">348</span> tests taken today</p>
              <p className="text-base text-[#2B2622]">Most common today: <span className="font-semibold text-[#9A2E1A]">Oval</span></p>
            </div>
          </div>

          {/* Why You Can Trust Section */}
          <div className="mb-6">
            <h2 className="text-2xl font-[600] font-[var(--funnel-cs-font-heading)] text-[#2B2622] mb-6 text-left tracking-wide">
              Why <span className="text-[#9A2E1A]">You Can Trust</span> Our Results
            </h2>
            
            <div className="space-y-4">
              {/* Trust Point 1 */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F3E2DB] rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#9A2E1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-[#2B2622] mb-1 text-left font-[var(--funnel-cs-font-body)]">Proven Face-Shape Styling System</h4>
                  <p className="text-sm text-[#6F665D] leading-relaxed text-left font-[var(--funnel-cs-font-body)]">
                    Based on facial proportions used in styling—so your hair, frames, and makeup feel balanced on your features.
                  </p>
                </div>
              </div>

              {/* Trust Point 2 */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F3E2DB] rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#9A2E1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-[#2B2622] mb-1 text-left font-[var(--funnel-cs-font-body)]">AI-Powered Proportions</h4>
                  <p className="text-sm text-[#6F665D] leading-relaxed text-left font-[var(--funnel-cs-font-body)]">
                    We analyze face length, cheekbones, and jawline balance (from your answers + optional photo) to match your closest shape.
                  </p>
                </div>
              </div>

              {/* Trust Point 3 */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#F3E2DB] rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-[#9A2E1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h4 className="font-bold text-[#2B2622] mb-1 text-left font-[var(--funnel-cs-font-body)]">Backed by 355,000+ Users</h4>
                  <p className="text-sm text-[#6F665D] leading-relaxed text-left font-[var(--funnel-cs-font-body)]">
                    Trusted by a global community using our guides to choose haircuts, glasses, and makeup faster.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Featured In Section */}
          <div className="text-left pt-6 border-t border-[#9A2E1A]/20">
            <h3 className="text-xl font-[600] font-[var(--funnel-cs-font-heading)] text-[#2B2622] mb-4 text-left tracking-wide">
              <span className="text-[#9A2E1A]">myStylist PRO</span> Has Been Featured In:
            </h3>
            <div className="flex items-center justify-start space-x-4 opacity-60">
              <img src={cosmoImg} alt="Cosmopolitan" className="h-8 grayscale" />
              <img src={vogueImg} alt="Vogue" className="h-4 grayscale" />
              <img src={marieclaireImg} alt="Marie Claire" className="h-16 grayscale" />
              <img src={elleImg} alt="Elle" className="h-8 grayscale" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center mb-8 px-4">
        <div className="max-w-[500px] text-center font-[var(--funnel-cs-font-body)]">
          <h2 className="text-2xl md:text-3xl font-[600] font-[var(--funnel-cs-font-heading)] text-[#2B2622] mb-4 leading-tight">
            Stop Wasting Precious Time
          </h2>
          <p className="text-lg text-[#6F665D] leading-relaxed">
            Discover your personalized face shape guide and start choosing hair, glasses, and makeup with confidence.
          </p>
        </div>
      </div>

      <div className="flex justify-center mb-4">
        <img src={beforeImg} alt="before" className="max-w-full w-[500px]" />
      </div>

      <div className="flex justify-center mb-4">
        <Button
          buttonStyles={`bg-[#9A2E1A] hover:bg-[#762514] text-[#FBF8F3] px-8 py-3 rounded-[14px] font-bold text-base max-w-[350px] mb-4 inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#9A2E1A] focus:ring-offset-2 transition-colors duration-200`}
          text="GET MY FACE SHAPE GUIDE NOW!"
          blick={false}
          action={() => onGetClick()}
        />
      </div>




      <div className="flex justify-center mb-4 -mx-4">
        <div className="w-full max-w-full">
          <div className="flex flex-col sm:flex-row">
            <div className="w-full sm:w-[48%] bg-white">
              <img
                src={analyzetImg}
                alt="analyzet"
                className="w-full max-h-[420px] sm:max-h-none object-contain select-none"
                draggable={false}
              />
            </div>

            <div className="w-full sm:w-[52%] bg-[#F6F1EB]">
              <div className="h-full flex items-center">
                <div className="w-full px-3 py-3 sm:px-5 sm:py-5 font-[var(--funnel-cs-font-body)] text-left">
                  <h3 className="text-[16px] sm:text-[18px] font-extrabold text-[#2B2622] leading-tight mb-3">
                    We&rsquo;ve Analyzed Your
                    <br />
                    Face Shape Profile:
                  </h3>

                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] sm:text-[15px] leading-none">✅</span>
                      <span className="text-[12px] sm:text-[13px] font-semibold text-[#2B2622] leading-snug">
                        Face Shape Match
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] sm:text-[15px] leading-none">✅</span>
                      <span className="text-[12px] sm:text-[13px] font-semibold text-[#2B2622] leading-snug">
                        Feature Balance (forehead / cheekbones / jawline)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] sm:text-[15px] leading-none">✅</span>
                      <span className="text-[12px] sm:text-[13px] font-semibold text-[#2B2622] leading-snug">
                        Glasses Fit Guide (frame shapes + sizing tips)
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[14px] sm:text-[15px] leading-none">✅</span>
                      <span className="text-[12px] sm:text-[13px] font-semibold text-[#2B2622] leading-snug">
                        Makeup Placement Map
                      </span>
                    </div>
                  </div>

                  <p className="text-[11px] sm:text-[12px] text-[#6F665D] leading-relaxed">
                    Your results go beyond guessing&mdash;get practical recommendations
                    that balance your proportions and flatter your features.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-center mb-8 px-4">
        <div className="max-w-[500px] text-center font-[var(--funnel-cs-font-body)]">
          <h2 className="text-2xl md:text-3xl font-[600] font-[var(--funnel-cs-font-heading)] text-[#2B2622] mb-6 leading-tight">
            How You'll Benefit
          </h2>
          
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-[#F3E2DB] rounded-lg flex items-center justify-center mb-3">
                <svg className="w-8 h-8 text-[#9A2E1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                </svg>
              </div>
              <p className="text-sm text-[#2B2622] text-center leading-relaxed">
                Look instantly more put-together with the right hair + frames
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-[#F3E2DB] rounded-lg flex items-center justify-center mb-3">
                <svg className="w-8 h-8 text-[#9A2E1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <p className="text-sm text-[#2B2622] text-center leading-relaxed">
                Apply makeup in the right places to lift your face
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-[#F3E2DB] rounded-lg flex items-center justify-center mb-3">
                <svg className="w-8 h-8 text-[#9A2E1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-[#2B2622] text-center leading-relaxed">
                Choose glasses faster—know what to look for when shopping
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 bg-[#F3E2DB] rounded-lg flex items-center justify-center mb-3">
                <svg className="w-8 h-8 text-[#9A2E1A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
              <p className="text-sm text-[#2B2622] text-center leading-relaxed">
                Feel more confident in photos and everyday life
              </p>
            </div>
          </div>
          
          <p className="text-base text-[#2B2622] leading-relaxed">
            Transform how you look and feel with a personalized face-shape guide.
          </p>
        </div>
      </div>

      <div className="h-[2px] w-full mb-4 bg-[#2B2622]"></div>

      <div className="flex justify-center mb-4 ">
        <img src={bloggersImg} alt="blog" className="max-w-full w-[500px]" />
      </div>

      <div className="flex justify-center mb-4">
        <Button
          buttonStyles={`bg-[#9A2E1A] hover:bg-[#762514] text-[#FBF8F3] px-8 py-3 rounded-[14px] font-bold text-base max-w-[350px] mb-4 inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#9A2E1A] focus:ring-offset-2 transition-colors duration-200`}
          text="GET MY FACE SHAPE GUIDE NOW!"
          blick={false}
          action={() => onGetClick()}
        />
      </div>

      {showPromoCode && (
        <div className="my-4 rounded-[8px] bg-[#F3E2DB] px-5 py-4">
          {discountBlockRender()}
        </div>
      )}

      {false && (
      <div>
        <div
          ref={discountBlockRef}
          className={
            "rounded-[8px] border-[2px] border-[#EAD9CD] px-4 pb-[22px] pt-8"
          }
        >
          <p className={"mb-6 text-[16px] leading-5 text-[#2B2622] text-left"}>
            We've helped thousands of women discover their style and glow with
            confidence, and now we want to help you too 🔥
          </p>
          <p
            className={
              "mb-6 text-[16px] font-[900] leading-5 text-[#2B2622] text-left"
            }
          >
            We believe money shouldn't be a barrier to feeling fabulous and
            finally solving wardrobe challenges once and for all. So, choose the
            amount you think is reasonable to try us for 1 week ❤️❤️❤️
          </p>

          <p
            className={
              "mb-6 text-[16px] font-[900] leading-5 text-[#9A2E1A] text-left"
            }
          >
            It costs us $9.78 to cover our expenses for the trial, but we want
            you to pick a price that you're comfortable with.
          </p>

          <div
            className={
              "mb-1 mr-3 flex justify-end text-[14px] leading-5 text-[#8A8075]"
            }
          >
            most popular choice*
          </div>

          <div className={"mb-6 flex justify-between"}>
            {landingPlans.map((item, index) => {
              return (
                <div
                  onClick={() => {
                    onPlanClick(item);
                  }}
                  className={`w-[48%] rounded-[8px] border border-[#EAD9CD] bg-[#F6F1EB] py-4 transition duration-300 ${
                    plan === item.value ? "!border-[#9A2E1A] !bg-[#F3E2DB]" : ""
                  }`}
                  key={`${index}-plan`}
                >
                  <p
                    className={
                      "text-center text-[16px] font-[900] leading-[22px]"
                    }
                  >
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mb-6">
            <Elements stripe={stripePromise}>
              <AppleGooglePayButton
                trialCost={plan}
            paymentAmount={pricing.amount}
            paymentCurrency={pricing.currency}
            paymentValue={pricing.purchaseValue}
                onSuccess={onSuccess}
                isDisabled={!plan}
              />
            </Elements>
          </div>

          {isApplePayAvailible === false ? (
            <div>
              <Button
                buttonStyles={
                  "bg-[#9A2E1A] hover:bg-[#762514] text-[#FBF8F3] px-8 py-3 rounded-[14px] font-bold text-base mb-4 inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#9A2E1A] focus:ring-offset-2 transition-colors duration-200"
                }
                text="GET MY FACE SHAPE GUIDE NOW!"
                isButtonDisabled={!plan}
                action={() => onPopupOpenClick()}
              />
              <p
                className={
                  "text-center text-[16px] leading-[22px] text-[#6F665D]"
                }
              >
                *7-day trial
              </p>

              <SubscriptionDisclaimer
                price={pricing.subscriptionDisclaimerPrice}
                className="text-[#8A8075] text-[12px] leading-[14px] text-left mt-2"
                isOrganicOrSeo={isOrganicDisclaimer}
                billingPeriod="every 3 months"
                showPrivacyLink={false}
              />
            </div>
          ) : (
            <div
              className={`relative ${
                plan === null ? "h-0 max-h-0" : "h-[auto] !max-h-[750px]"
              } transiton overflow-hidden duration-300`}
            >
              <div className={`relative`}>
                <div>{renderFullForm(true)}</div>
              </div>

              <div>
                <p
                  onClick={() => setPopupOpen(true)}
                  className="cursor-pointer text-center text-[16px] font-[500] text-[#2B2622] underline"
                >
                  Continue with other payment options
                </p>
              </div>

              <SubscriptionDisclaimer
                price={pricing.subscriptionDisclaimerPrice}
                className="text-[#8A8075] text-[12px] leading-[14px] text-left mt-2"
                isOrganicOrSeo={isOrganicDisclaimer}
                billingPeriod="every 3 months"
                showPrivacyLink={false}
              />
            </div>
          )}
        </div>
      </div>
      )}

      <div className="flex justify-center mb-10 ">
        <img src={visaImg} alt="visa" className="max-w-full w-[500px]" />
      </div>

      {isPopupOpen ? (
        <Popup
          content={<div className="pt-4">{renderPopupForm()}</div>}
          isOpen={isPopupOpen}
          closeFunction={() => onClosePopupClick()}
        />
      ) : null}

      {/* Sticky Bottom Button */}
      {showStickyButton && (
        <div className={`fixed bottom-0 left-0 right-0 z-50 transition-all duration-500 ${showStickyButton ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>
          <div className="w-full bg-[#FBF8F3] border-t border-[#EAD9CD] px-4 py-4 shadow-lg">
            <div className="max-w-sm mx-auto">
              <Button
                buttonStyles={`w-full bg-[#9A2E1A] hover:bg-[#762514] text-[#FBF8F3] px-6 py-3 rounded-[14px] font-bold text-base inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#9A2E1A] focus:ring-offset-2 transition-all duration-300 shadow-lg transform animate-pulse-scale hover:animate-none hover:scale-105`}
                text="GET MY FACE SHAPE GUIDE NOW!"
                blick={false}
                action={() => onGetClick()}
              />
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default PaywallFaceShape;
